import { useCallback, useEffect, useRef, useState } from "react";
import { workDescriptionAssist } from "@/features/timesheetFlow/timesheetFlowApi";

const DEBOUNCE_MS = 2000;
const MIN_LENGTH = 3;

export function useWorkDescriptionAssist(text: string) {
  const [loading, setLoading] = useState(false);
  const [suggestedText, setSuggestedText] = useState<string | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seqRef = useRef(0);
  const lastAppliedRef = useRef<string | null>(null);

  const clearAssist = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    lastAppliedRef.current = null;
    seqRef.current += 1;
    setLoading(false);
    setSuggestedText(null);
    setReason(null);
  }, []);

  const applySuggestion = useCallback(
    (onApply: (next: string) => void) => {
      const next = suggestedText?.trim();
      if (!next) return;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      seqRef.current += 1;
      onApply(next);
      lastAppliedRef.current = next;
      setLoading(false);
      setSuggestedText(null);
      setReason(null);
    },
    [suggestedText],
  );

  const onUserEdit = useCallback(() => {
    lastAppliedRef.current = null;
  }, []);

  useEffect(() => {
    if (lastAppliedRef.current !== null && text === lastAppliedRef.current) {
      return;
    }

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const trimmed = text.trim();
      if (trimmed.length < MIN_LENGTH) {
        clearAssist();
        return;
      }

      const seq = ++seqRef.current;
      setLoading(true);
      setSuggestedText(null);
      setReason(null);

      void workDescriptionAssist(trimmed)
        .then((data) => {
          if (seq !== seqRef.current) return;

          const suggestion = String(data.suggested_text || "").trim();
          setSuggestedText(suggestion || null);

          const r = String(data.reason || "").trim();
          setReason(r || null);
        })
        .catch((err: unknown) => {
          if (seq === seqRef.current) {
            console.warn("Stundennachweis Textassist:", err);
            setSuggestedText(null);
            setReason(null);
          }
        })
        .finally(() => {
          if (seq === seqRef.current) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [text, clearAssist]);

  const resetForLoadedText = useCallback((loaded: string) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    seqRef.current += 1;
    lastAppliedRef.current = loaded;
    setLoading(false);
    setSuggestedText(null);
    setReason(null);
  }, []);

  return {
    loading,
    suggestedText,
    reason,
    applySuggestion,
    onUserEdit,
    clearAssist,
    resetForLoadedText,
  };
}
